import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth/auth-provider';
import { queryKeys } from '@/lib/query-keys';

/** Public runtime configuration (feature flags, limits). Cached for the session. */
export function useConfig() {
  return useQuery({
    queryKey: queryKeys.config,
    queryFn: () => api.config(),
    staleTime: 5 * 60_000,
  });
}

export function useFeature(
  key:
    | 'ai'
    | 'source_search'
    | 'speech'
    | 'public_sharing'
    | 'image_import'
    | 'headless_scraper'
    | 'tasting_notes'
    | 'ingredient_catalog'
    | 'barcode_scan'
    | 'recipe_nutrition'
    | 'shopping_live'
    | 'support_inbox'
    | 'prices'
    | 'price_tag_scan'
    | 'receipt_scan'
    | 'custom_backgrounds'
    | 'ai_chat',
) {
  const { data } = useConfig();
  // Until the config is known we assume the feature is on so the UI does not flicker.
  return data ? Boolean(data.features?.[key]) : true;
}

export function useProfile(enabled = true) {
  const { status } = useAuth();
  return useQuery({
    queryKey: queryKeys.profile,
    queryFn: () => api.users.profile(),
    enabled: enabled && status === 'authenticated',
  });
}

/** Whether an administrator has unlocked AI features for the current user. */
export function useAiUnlocked(): boolean {
  const { user } = useAuth();
  return Boolean(user?.aiEnabled);
}

/**
 * Whether the current user may use AI features: unlocked by an administrator and consented
 * (auth user is the source of truth). A stored consent does not count while AI is locked.
 */
export function useAiConsent(): boolean {
  const { user } = useAuth();
  return Boolean(user?.aiEnabled && user.aiConsentAt);
}

export function useHouseholds() {
  const { status } = useAuth();
  return useQuery({
    queryKey: queryKeys.households.all,
    queryFn: () => api.households.list(),
    enabled: status === 'authenticated',
  });
}
